import { BASE_URL, DONATION_ROUTES } from '@/constants';
import { getStaticCdnUrl } from '@/utils';
import { checkTodayInRange } from '@/utils/checkTodayInRange';

// --- Shinhan helpers (private) ---

const SHINHAN_UTM = {
  source: 'shinhancard',
  medium: 'affiliate',
  campaign: 'donation',
} as const;

const getShinhanUtmSearchParams = (content: string) =>
  new URLSearchParams({
    utm_source: SHINHAN_UTM.source,
    utm_medium: SHINHAN_UTM.medium,
    utm_campaign: SHINHAN_UTM.campaign,
    utm_content: content,
  });

const SHINHAN_MARCH_EVENT_URL =
  'https://www.shinhancard.com/pconts/html/benefit/event/1237843_2239.html';

const SHINHAN_APRIL_EVENT_URL =
  'https://www.shinhancard.com/pconts/html/benefit/event/1238318_2239.html';

const SHINHAN_MAY_EVENT_URL =
  'https://www.shinhancard.com/pconts/html/benefit/event/1238747_2239.html';

const shinhanMarchUtmParams = getShinhanUtmSearchParams('donation_event_260301');
const shinhanMarchUtmQuery = shinhanMarchUtmParams.toString();
const shinhanMarchSearchParams = Object.fromEntries(
  shinhanMarchUtmParams.entries(),
);

const shinhanAprilUtmParams = getShinhanUtmSearchParams('donation_event_260401');
const shinhanAprilUtmQuery = shinhanAprilUtmParams.toString();
const shinhanAprilSearchParams = Object.fromEntries(
  shinhanAprilUtmParams.entries(),
);

const shinhanMayUtmParams = getShinhanUtmSearchParams('donation_event_260126');
const shinhanMayUtmQuery = shinhanMayUtmParams.toString();
const shinhanMaySearchParams = Object.fromEntries(
  shinhanMayUtmParams.entries(),
);

const getShinhanMainBannerUrl = (
  eventUrl: string,
  utmQuery: string,
  searchParams: Record<string, string>,
): string => {
  const payload = JSON.stringify({
    pc: { pathname: eventUrl, searchParams },
    mobile: { pathname: eventUrl, searchParams },
  });

  return `${BASE_URL}/screen/redirect?${utmQuery}&url=${encodeURIComponent(payload)}`;
};

// --- Config types & array ---

type CardPromotionConfig = {
  id: string;
  title: string;
  range: readonly [string, string];
  imageUrl: string;
  inlineImageUrl: string;
  inlineAlt: string;
  href: string;
  mainHref?: string;
  inlineHref?: string;
  target?: '_blank';
};

const CARD_PROMOTION_CONFIGS: CardPromotionConfig[] = [
  {
    id: 'kb',
    title: 'KB국민카드X웰로',
    range: ['2026-03-01', '2026-05-31'],
    imageUrl: getStaticCdnUrl('kb-card-event/donation-home-kb-card-banner.png'),
    inlineImageUrl: getStaticCdnUrl(
      'kb-card-event/donation-order-detail-kb-card-banner-pc.png',
    ),
    inlineAlt: 'KB국민카드 체크카드 이벤트 배너',
    href: DONATION_ROUTES.KB_CHECK_CARD.pathname,
  },
  {
    id: 'shinhan',
    title: '신한카드X웰로',
    range: ['2026-03-01', '2026-03-31'],
    imageUrl: getStaticCdnUrl('banner/shinhancard.png'),
    inlineImageUrl: getStaticCdnUrl('donation/donation-wello-x-sinhan.png'),
    inlineAlt: '웰로x신한카드 이벤트 배너',
    href: `${SHINHAN_MARCH_EVENT_URL}?${shinhanMarchUtmQuery}`,
    mainHref: getShinhanMainBannerUrl(
      SHINHAN_MARCH_EVENT_URL,
      shinhanMarchUtmQuery,
      shinhanMarchSearchParams,
    ),
    target: '_blank',
  },
  {
    id: 'shinhan',
    title: '신한카드X웰로',
    range: ['2026-04-01', '2026-04-30'],
    imageUrl: getStaticCdnUrl('banner/shinhancard.png'),
    inlineImageUrl: getStaticCdnUrl('donation/donation-wello-x-sinhan.png'),
    inlineAlt: '웰로x신한카드 이벤트 배너',
    href: `${SHINHAN_APRIL_EVENT_URL}?${shinhanAprilUtmQuery}`,
    mainHref: getShinhanMainBannerUrl(
      SHINHAN_APRIL_EVENT_URL,
      shinhanAprilUtmQuery,
      shinhanAprilSearchParams,
    ),
    target: '_blank',
  },
  {
    id: 'shinhan',
    title: '신한카드X웰로',
    range: ['2026-05-01', '2026-05-31'],
    imageUrl: getStaticCdnUrl('banner/shinhancard.png'),
    inlineImageUrl: getStaticCdnUrl('donation/donation-wello-x-sinhan.png'),
    inlineAlt: '웰로x신한카드 이벤트 배너',
    href: `${SHINHAN_MAY_EVENT_URL}?${shinhanMayUtmQuery}`,
    mainHref: getShinhanMainBannerUrl(
      SHINHAN_MAY_EVENT_URL,
      shinhanMayUtmQuery,
      shinhanMaySearchParams,
    ),
    target: '_blank',
  },
];

// --- Derived exports ---

const activeConfigs = CARD_PROMOTION_CONFIGS.filter((c) =>
  checkTodayInRange(c.range[0], c.range[1]),
);

const configById = Object.fromEntries(
  activeConfigs.map((c) => [c.id, c]),
) as Record<string, CardPromotionConfig>;

const isPromotionActive = (id: string): boolean =>
  activeConfigs.some((c) => c.id === id);

export const isShinhanActive = isPromotionActive('shinhan');

export const CARD_PROMOTIONS = {
  configs: configById,
  inline: activeConfigs.map((c) => ({
    href: c.inlineHref ?? c.href,
    imageUrl: c.inlineImageUrl,
    alt: c.inlineAlt,
    target: c.target,
  })),
  main: activeConfigs.map((c) => ({
    imageUrl: c.imageUrl,
    href: c.mainHref ?? c.href,
    title: c.title,
    target: c.target,
  })),
};
